import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from '@jira-clone/ui'
import { getApiErrorMessage } from '../../../shared/utils/apiError'
import { addDays, formatDuration, shiftWeek, STATUS_LABEL, weekLabel, weekStartOf } from '../../../lib/timesheet'
import { downloadTimesheetCsv, useComplianceReport, useHoursReport, useVarianceReport } from '../api'

const INPUT = 'h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring'

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border bg-card p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  )
}

function Table({ title, headers, children, empty }: { title: string; headers: string[]; children: React.ReactNode; empty?: boolean }) {
  return (
    <section className="rounded-xl border bg-card overflow-hidden" aria-label={title}>
      <h3 className="px-4 py-2.5 text-sm font-semibold border-b">{title}</h3>
      {empty ? (
        <p className="px-4 py-6 text-sm text-muted-foreground text-center">Nothing in this range.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                {headers.map((h, i) => (
                  <th key={h} className={`px-4 py-2 font-medium ${i > 0 ? 'text-right' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">{children}</tbody>
          </table>
        </div>
      )}
    </section>
  )
}

const Bar = ({ value, max }: { value: number; max: number }) => (
  <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden" aria-hidden="true">
    <div className="h-full bg-primary" style={{ width: `${max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0}%` }} />
  </div>
)

function Range({
  from,
  to,
  onChange,
  drafts,
  onDrafts,
  onExport,
  exporting,
}: {
  from: string
  to: string
  onChange: (from: string, to: string) => void
  drafts?: boolean
  onDrafts?: (v: boolean) => void
  onExport?: () => void
  exporting?: boolean
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-xs space-y-1">
        <span className="block text-muted-foreground">From</span>
        <input type="date" className={INPUT} value={from} max={to} onChange={(e) => e.target.value && onChange(e.target.value, to)} aria-label="From" />
      </label>
      <label className="text-xs space-y-1">
        <span className="block text-muted-foreground">To</span>
        <input type="date" className={INPUT} value={to} min={from} onChange={(e) => e.target.value && onChange(from, e.target.value)} aria-label="To" />
      </label>
      {onDrafts && (
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" className="h-4 w-4" checked={!!drafts} onChange={(e) => onDrafts(e.target.checked)} />
          Include weeks not yet submitted
        </label>
      )}
      {onExport && (
        <Button size="sm" variant="outline" className="ml-auto" disabled={exporting} onClick={onExport}>
          <Download className="h-4 w-4 mr-1" /> {exporting ? 'Preparing…' : 'Export CSV'}
        </Button>
      )}
    </div>
  )
}

function Hours({ today }: { today: string }) {
  const [range, setRange] = useState({ from: `${today.slice(0, 7)}-01`, to: addDays(weekStartOf(today), 6) })
  const [drafts, setDrafts] = useState(false)
  const [exporting, setExporting] = useState(false)
  const report = useHoursReport(range.from, range.to, drafts)
  const d = report.data

  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadTimesheetCsv(range.from, range.to, drafts)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'export the timesheets'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <Range from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} drafts={drafts} onDrafts={setDrafts} onExport={exportCsv} exporting={exporting} />
      {report.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {report.error && <p role="alert" className="text-sm text-destructive">{getApiErrorMessage(report.error, 'load this report')}</p>}
      {d && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Time logged" value={formatDuration(d.totalMinutes)} hint={drafts ? 'Including weeks not yet submitted' : 'Submitted and approved weeks'} />
            <Stat label="People" value={String(d.people)} hint="who logged time" />
            <Stat label="Expected" value={formatDuration(d.expectedMinutes)} hint="for the submitted weeks" />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Table title="By department" headers={['Department', 'People', 'Time']} empty={d.byDepartment.length === 0}>
              {d.byDepartment.map((r) => (
                <tr key={r.department}>
                  <td className="px-4 py-2">
                    {r.department}
                    <Bar value={r.minutes} max={d.byDepartment[0].minutes} />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.people}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatDuration(r.minutes)}</td>
                </tr>
              ))}
            </Table>
            <Table title="By project" headers={['Project', 'Time']} empty={d.byProject.length === 0}>
              {d.byProject.map((r) => (
                <tr key={r.projectId}>
                  <td className="px-4 py-2">
                    {r.name}
                    <Bar value={r.minutes} max={d.byProject[0].minutes} />
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{formatDuration(r.minutes)}</td>
                </tr>
              ))}
            </Table>
          </div>
          <Table title="By person" headers={['Person', 'Logged', 'Expected', 'Leave and holidays']} empty={d.byPerson.length === 0}>
            {d.byPerson.map((r) => (
              <tr key={r.userId}>
                <td className="px-4 py-2">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.department ?? ''}</p>
                </td>
                <td className="px-4 py-2 text-right font-mono">{formatDuration(r.minutes)}</td>
                <td className="px-4 py-2 text-right font-mono text-muted-foreground">{r.expectedMinutes ? formatDuration(r.expectedMinutes) : '—'}</td>
                <td className="px-4 py-2 text-right font-mono text-muted-foreground">{r.coverMinutes ? formatDuration(r.coverMinutes) : '—'}</td>
              </tr>
            ))}
          </Table>
          <Table title="By activity" headers={['Activity', 'Department', 'Time']} empty={d.byActivity.length === 0}>
            {d.byActivity.map((r) => (
              <tr key={`${r.department}-${r.activity}`}>
                <td className="px-4 py-2">{r.activity}</td>
                <td className="px-4 py-2 text-right text-muted-foreground">{r.department}</td>
                <td className="px-4 py-2 text-right font-mono">{formatDuration(r.minutes)}</td>
              </tr>
            ))}
          </Table>
          {d.byCategory.length > 0 && (
            <p className="text-xs text-muted-foreground">
              By type: {d.byCategory.map((c) => `${c.label} ${formatDuration(c.minutes)}`).join(' · ')}
            </p>
          )}
        </>
      )}
    </div>
  )
}

function Compliance({ today }: { today: string }) {
  const currentWeek = weekStartOf(today)
  const [week, setWeek] = useState(currentWeek)
  const report = useComplianceReport(week)
  const d = report.data
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" aria-label="Previous week" onClick={() => setWeek(shiftWeek(week, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="min-w-[12rem] text-center text-sm font-semibold">{weekLabel(week)}</p>
        <Button size="icon" variant="outline" aria-label="Next week" disabled={week >= currentWeek} onClick={() => setWeek(shiftWeek(week, 1))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      {report.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {report.error && <p role="alert" className="text-sm text-destructive">{getApiErrorMessage(report.error, 'load this report')}</p>}
      {d && (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Submitted" value={`${d.totals.submitted} of ${d.totals.people}`} hint={`${d.totals.submittedPercent}%`} />
            <Stat label="Approved" value={String(d.totals.approved)} />
            <Stat label="On time" value={String(d.totals.onTime)} hint="by the Monday after the week" />
            <Stat label="Missing" value={String(d.totals.missing)} />
          </div>
          <Table title="Who has submitted" headers={['Person', 'Status', 'Logged', 'Expected', 'Submitted']} empty={d.people.length === 0}>
            {d.people.map((p) => (
              <tr key={p.userId}>
                <td className="px-4 py-2">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.department ?? ''}</p>
                </td>
                <td className="px-4 py-2 text-right">
                  <Badge variant={p.status === 'approved' ? 'success' : p.status === 'submitted' ? 'outline' : p.status === 'none' ? 'destructive' : 'secondary'} className="text-[10px] uppercase">
                    {p.status === 'none' ? 'Not started' : STATUS_LABEL[p.status]}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-right font-mono">{formatDuration(p.workedMinutes)}</td>
                <td className="px-4 py-2 text-right font-mono text-muted-foreground">{p.expectedMinutes != null ? formatDuration(p.expectedMinutes) : '—'}</td>
                <td className="px-4 py-2 text-right text-xs text-muted-foreground">
                  {p.submittedAt ? new Date(p.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '—'}
                  {p.onTime === false && <span className="text-amber-600"> · {p.daysLate} {p.daysLate === 1 ? 'day' : 'days'} late</span>}
                </td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

const FLAG: Record<string, { label: string; className: string }> = {
  ok: { label: 'Close', className: 'text-emerald-600' },
  over: { label: 'More than attended', className: 'text-amber-600' },
  under: { label: 'Less than attended', className: 'text-amber-600' },
  no_attendance: { label: 'No attendance', className: 'text-destructive' },
  no_timesheet: { label: 'No timesheet', className: 'text-destructive' },
}

function Variance({ today }: { today: string }) {
  const [range, setRange] = useState(() => {
    const monday = weekStartOf(today)
    return { from: shiftWeek(monday, -1), to: addDays(monday, 6) }
  })
  const report = useVarianceReport(range.from, range.to)
  const d = report.data
  return (
    <div className="space-y-4">
      <Range from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />
      <p className="text-xs text-muted-foreground">
        Time logged on timesheets (submitted or not) against time checked in at work. A gap is worth a conversation, not a conclusion: meetings away from a desk count on one side only.
      </p>
      {report.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {report.error && <p role="alert" className="text-sm text-destructive">{getApiErrorMessage(report.error, 'load this report')}</p>}
      {d && (
        <Table title="Timesheet against attendance" headers={['Person', 'Timesheet', 'Attendance', 'Difference', 'Note']} empty={d.people.length === 0}>
          {d.people.map((p) => (
            <tr key={p.userId}>
              <td className="px-4 py-2">
                <p className="font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.department ?? ''}</p>
              </td>
              <td className="px-4 py-2 text-right font-mono">{formatDuration(p.timesheetMinutes)}</td>
              <td className="px-4 py-2 text-right font-mono">{formatDuration(p.attendanceMinutes)}</td>
              <td className="px-4 py-2 text-right font-mono">
                {p.differenceMinutes > 0 ? '+' : p.differenceMinutes < 0 ? '-' : ''}
                {formatDuration(Math.abs(p.differenceMinutes))}
              </td>
              <td className={`px-4 py-2 text-right text-xs font-medium ${FLAG[p.flag].className}`}>{FLAG[p.flag].label}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}

/** The Super Admin's timesheet reports: hours, who has submitted, and timesheet against attendance. */
export function ReportsView({ today }: { today: string }) {
  return (
    <Tabs defaultValue="hours">
      <TabsList>
        <TabsTrigger value="hours">Hours</TabsTrigger>
        <TabsTrigger value="compliance">Who submitted</TabsTrigger>
        <TabsTrigger value="variance">Against attendance</TabsTrigger>
      </TabsList>
      <TabsContent value="hours" className="mt-4">
        <Hours today={today} />
      </TabsContent>
      <TabsContent value="compliance" className="mt-4">
        <Compliance today={today} />
      </TabsContent>
      <TabsContent value="variance" className="mt-4">
        <Variance today={today} />
      </TabsContent>
    </Tabs>
  )
}
